import { ArtifactText, artifactKind, TEXT_KINDS } from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import type { BrowserArtifacts, EncryptedArtifactRef } from '../lib/encrypted-artifacts.js'

type Reader = Pick<BrowserArtifacts, 'open'>
type Handle = Awaited<ReturnType<BrowserArtifacts['open']>>
export function useOpenedArtifact(reader: Reader, reference: EncryptedArtifactRef | undefined) {
  const { accountId, epoch, objectId, runId, version } = reference ?? {}
  const coordinate = JSON.stringify([accountId, epoch, objectId, runId, version])
  const [state, setState] = useState<{
    reader: Reader
    coordinate: string
    handle?: Handle
    failed?: boolean
  }>()
  useEffect(() => {
    if (!accountId || !epoch || !objectId || !runId || !version) return
    let cancelled = false
    let handle: Handle | undefined
    void reader.open({ accountId, epoch, objectId, runId, version }).then(
      (value) => {
        if (cancelled) value.dispose()
        else {
          handle = value
          setState({ reader, coordinate, handle: value })
        }
      },
      () => {
        if (!cancelled) setState({ reader, coordinate, failed: true })
      },
    )
    return () => {
      cancelled = true
      handle?.dispose()
    }
  }, [reader, coordinate, accountId, epoch, objectId, runId, version])
  // An account/reference change must not display the previous URL before effect cleanup.
  return state?.reader === reader && state.coordinate === coordinate ? state : undefined
}

/**
 * Plain text of a decrypted (octet-stream) blob, rendered as text — never as a document, so
 * S05 holds: an agent-written file cannot become app-origin markup.
 */
function DecryptedText({
  url,
  blob,
  name,
  mime,
}: {
  url: string
  blob?: Blob | undefined
  name: string
  mime: string
}) {
  const [text, setText] = useState<string | null>()
  useEffect(() => {
    let cancelled = false
    // Read the decrypted bytes in place; a fetch of the blob: URL is blocked by connect-src.
    ;(blob ? blob.text() : fetch(url).then((r) => r.text())).then(
      (t) => !cancelled && setText(t),
      () => !cancelled && setText(null),
    )
    return () => {
      cancelled = true
    }
  }, [url, blob])
  if (text === undefined) return <span>Loading preview…</span>
  if (text === null) return <span role="status">Preview unavailable</span>
  return <ArtifactText text={text} kind={artifactKind(mime, name)} />
}

/** Endpoint-only media; no signed URL or plaintext download fallback. */
export function EncryptedArtifact({
  reader,
  reference,
  thumbnailReference,
  textPreview = false,
}: {
  reader: Reader
  reference: EncryptedArtifactRef
  thumbnailReference?: EncryptedArtifactRef
  /** Show text-like files (markdown, JSON, plain text) inline as text above the download. */
  textPreview?: boolean
}) {
  const main = useOpenedArtifact(reader, reference)
  const thumbnail = useOpenedArtifact(reader, thumbnailReference)
  const opened = main?.handle
  const poster = thumbnail?.handle
  if (main?.failed || thumbnail?.failed || (poster && !poster.preview?.startsWith('image/')))
    return <span role="alert">Attachment locked or unavailable</span>
  if (!opened) return <span>Loading attachment…</span>
  // Only passive previewable types render inline (S05); the rest is a download.
  if (opened.preview?.startsWith('image/')) return <img src={opened.url} alt={opened.name} />
  if (opened.preview?.startsWith('video/'))
    return (
      // biome-ignore lint/a11y/useMediaCaption: Uploaded media does not include a caption track.
      <video
        src={opened.url}
        {...(poster ? { poster: poster.url } : {})}
        controls
        aria-label={opened.name}
      />
    )
  const download = (
    <a href={opened.url} download={opened.name}>
      {opened.name}
    </a>
  )
  if (textPreview && TEXT_KINDS.has(artifactKind(opened.mime, opened.name)))
    return (
      <>
        <DecryptedText url={opened.url} blob={opened.blob} name={opened.name} mime={opened.mime} />
        <p className="vk-meta">Download {download}</p>
      </>
    )
  return download
}
